import { Shape, SpotOrientation } from "../../../types";

/**
 * Transform companion positions when spot orientation changes.
 *
 * Same-axis changes (left↔right, top↔bottom) mirror companion positions.
 * Cross-axis changes (left/right ↔ top/bottom) rotate companions 90° and
 * swap their width/height so the composition layout stays consistent.
 */
export function transformCompanions(
  companions: Shape[],
  spotWidth: number,
  spotHeight: number,
  fromOrientation: SpotOrientation,
  toOrientation: SpotOrientation
): Shape[] {
  if (fromOrientation === toOrientation || companions.length === 0) {
    return companions;
  }

  const isFromVertical =
    fromOrientation === "left" || fromOrientation === "right";
  const isToVertical = toOrientation === "left" || toOrientation === "right";
  const sameAxis = isFromVertical === isToVertical;

  if (sameAxis) {
    if (isFromVertical) {
      // Horizontal mirror (left↔right)
      return companions.map((c) => ({
        ...c,
        x: spotWidth - c.x - c.width
      }));
    }
    // Vertical mirror (top↔bottom)
    return companions.map((c) => ({
      ...c,
      y: spotHeight - c.y - c.height
    }));
  }

  // Cross-axis: rotate 90° CW or CCW around spot center
  // CW chain (visual clockwise on screen): left → top → right → bottom → left
  const isCW =
    (fromOrientation === "left" && toOrientation === "top") ||
    (fromOrientation === "top" && toOrientation === "right") ||
    (fromOrientation === "right" && toOrientation === "bottom") ||
    (fromOrientation === "bottom" && toOrientation === "left");

  const cx = spotWidth / 2;
  const cy = spotHeight / 2;

  return companions.map((c) => {
    const dx = c.x + c.width / 2 - cx;
    const dy = c.y + c.height / 2 - cy;

    // Visual CW on screen (y-down): (dx,dy) → (-dy, dx)
    // Visual CCW:                    (dx,dy) → (dy, -dx)
    const newDx = isCW ? -dy : dy;
    const newDy = isCW ? dx : -dx;
    const newW = c.height;
    const newH = c.width;

    return {
      ...c,
      x: newDx + cx - newW / 2,
      y: newDy + cy - newH / 2,
      width: newW,
      height: newH
    };
  });
}
